import { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, Edit2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "@/hooks/use-toast";
import { formatCurrency } from "@/lib/currencyConversion";
import { useLanguage } from "@/contexts/LanguageContext";
import { useGamification } from "@/contexts/GamificationContext";
import { useBudget } from "@/contexts/BudgetContext";
import { useAuth } from "@/contexts/AuthContext";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import { BudgetDisplaySkeleton, BudgetDisplayMinimizedSkeleton } from "./Loading/BudgetDisplaySkeleton";
import { DashboardCard } from "./ui/dashboard-card";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "./ui/drawer";
interface BudgetDisplayProps {
  refreshTrigger: number;
  minimized?: boolean;
  userId?: string;
}

export const BudgetDisplay = ({ refreshTrigger, minimized = false }: BudgetDisplayProps) => {
  const { data, isLoading, refresh } = useBudget();
  const { user } = useAuth();
  const { t } = useLanguage();
  const { settings } = useGamification();
  const calmMode = settings?.hideRewards ?? false;

  const [displaySpent, setDisplaySpent] = useState(0);
  const [editPopoverOpen, setEditPopoverOpen] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  // Refresh budget when refreshTrigger changes
  useEffect(() => {
    refresh();
  }, [refreshTrigger, refresh]);

  // Animate number change — capture target at effect start to avoid stale closure
  useEffect(() => {
    if (!data) return;
    
    const target = data.spent;
    const duration = 500;
    const steps = 20;
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      if (currentStep >= steps) {
        setDisplaySpent(target);
        clearInterval(timer);
      } else {
        setDisplaySpent(prev => prev + (target - prev) / (steps - currentStep + 1));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [data?.spent]);

  const handleOpenEdit = () => {
    if (!data) return;
    setEditValue(data.monthlyBudget.toString());
    setEditPopoverOpen(true);
  };

  const handleSaveBudget = async () => {
    const newBudget = parseFloat(editValue);
    if (isNaN(newBudget) || newBudget <= 0) {
      toast({
        title: t('budget.invalidAmount'),
        description: t('budget.enterValid'),
        variant: "destructive",
      });
      return;
    }

    try {
      if (!user) return;

      const { error } = await supabase
        .from('budget_settings')
        .upsert({
          user_id: user.id,
          monthly_budget: newBudget,
        });

      if (error) throw error;

      setEditPopoverOpen(false);
      refresh();
      toast({
        title: t('budget.updated'),
        description: t('budget.monthlyBudgetSet', { amount: formatCurrency(newBudget, data?.currency || 'EUR') }),
      });
    } catch (error) {
      console.error('Update budget error:', error);
      toast({
        title: t('budget.updateFailed'),
        description: t('budget.couldNotUpdate'),
        variant: "destructive",
      });
    }
  };

  // Use context data or defaults
  const monthlyBudget = data?.monthlyBudget ?? 1000;
  const spent = displaySpent;
  const currency = data?.currency ?? 'EUR';
  const remaining = monthlyBudget - spent;
  const percentUsed = (spent / monthlyBudget) * 100;
  const isOverBudget = remaining < 0;
  const daysLeft = data?.daysLeft ?? 30;
  const dailyBudget = remaining > 0 ? remaining / daysLeft : 0;

  // Get neutral or positive colors - no anxiety-inducing yellows/oranges
  const getCardBorderColor = () => {
    if (isOverBudget) return 'border-destructive/30';
    return 'border-primary/20';
  };

  const getDailyBudgetColor = () => {
    if (isOverBudget) return 'text-destructive';
    return 'text-primary';
  };

  const getProgressBarColor = () => {
    if (isOverBudget) return 'bg-destructive';
    if (percentUsed > 90) return 'bg-destructive';
    return 'bg-primary';
  };

  // Show skeleton while loading
  if (isLoading && minimized) {
    return <BudgetDisplayMinimizedSkeleton />;
  }
  
  if (isLoading) {
    return <BudgetDisplaySkeleton />;
  }

  // Minimized view for chat (keep existing behavior)
  if (minimized && !isExpanded) {
    return (
      <div 
        onClick={() => setIsExpanded(true)}
        className="sticky top-0 z-10 bg-card border-b border-border px-4 py-4 cursor-pointer hover:bg-accent/50 transition-colors"
      >
        <div className="flex items-center justify-between max-w-2xl mx-auto">
          <div>
            <p className="text-label-card">{t('budget.canSpend')}</p>
            <p className={`text-amount-medium ${getDailyBudgetColor()}`}>
              {formatCurrency(dailyBudget, currency)}/day
            </p>
          </div>
          <ChevronDown className="h-5 w-5 text-muted-foreground" />
        </div>
      </div>
    );
  }

  // Minimized expanded view for chat
  if (minimized && isExpanded) {
    return (
      <div className="sticky top-0 z-10 bg-card border-b border-border shadow-md animate-scale-in">
        <div className="p-6 max-w-2xl mx-auto">
          <div className="flex items-start justify-between mb-4">
            <h3 className="text-label-card">Safe to Spend Today</h3>
            <Button variant="ghost" size="sm" onClick={() => setIsExpanded(false)} className="h-8 w-8 p-0">
              <ChevronUp className="h-4 w-4" />
            </Button>
          </div>
          
          <p className={`text-amount-hero mb-4 ${getDailyBudgetColor()}`}>
            {formatCurrency(dailyBudget, currency)}
          </p>

          <div className="space-y-2">
            <div className="h-2 bg-secondary rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all duration-500 ${getProgressBarColor()}`}
                style={{ width: `${Math.min(percentUsed, 100)}%` }}
              />
            </div>
            <p className="text-sm text-muted-foreground">
              {formatCurrency(spent, currency)} spent of {formatCurrency(monthlyBudget, currency)}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // FULL VIEW - Redesigned for ADHD users (Simplified)
  return (
    <DashboardCard variant={isOverBudget ? "destructive" : "default"} noPadding>
      {/* Primary Focus: Daily Safe-to-Spend */}
      <div className="px-6 pt-8 pb-6 text-center">
        <p className="text-label-card mb-2">
          Safe to Spend Today
        </p>
        
        {/* THE ONE BIG NUMBER */}
        <motion.p
          key={dailyBudget}
          initial={calmMode ? {} : { scale: 0.95, opacity: 0.8 }}
          animate={calmMode ? {} : { scale: 1, opacity: 1 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className={`text-amount-hero ${getDailyBudgetColor()}`}
        >
          {isLoading ? '...' : formatCurrency(dailyBudget, currency)}
        </motion.p>

        {isOverBudget && (
          <p className="text-sm text-muted-foreground mt-2">
            Over budget by {formatCurrency(Math.abs(remaining), currency)}
          </p>
        )}
      </div>

      {/* Progress Bar - Clean, larger for visibility */}
      <div className="px-6 pb-4">
        <div 
          className="h-3 bg-secondary rounded-full overflow-hidden"
          role="progressbar"
          aria-valuenow={percentUsed}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${percentUsed.toFixed(0)}% of budget used`}
        >
          <motion.div
            className={`h-full ${getProgressBarColor()}`}
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(percentUsed, 100)}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
        </div>
        <div className="flex items-center justify-center gap-2 mt-2">
          <p className="text-meta">
            {percentUsed.toFixed(0)}% of month used
          </p>
          <button 
            onClick={handleOpenEdit}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md hover:bg-accent transition-colors text-muted-foreground text-sm"
            aria-label="Edit budget"
          >
            <Edit2 className="h-3 w-3" />
          </button>
        </div>
      </div>

      {/* Edit Budget Drawer - Simplified */}
      <Drawer open={editPopoverOpen} onOpenChange={setEditPopoverOpen}>
        <DrawerContent className="max-h-[85vh]">
          <DrawerHeader className="text-center pb-2">
            <DrawerTitle className="text-xl">
              {t('budget.editMonthlyBudget')}
            </DrawerTitle>
            <DrawerDescription>
              {t('budget.setYourMonthlyLimit')}
            </DrawerDescription>
          </DrawerHeader>

          <div className="px-6 pb-6 space-y-6">
            {/* Input Field - Focus immediately */}
            <div className="space-y-2">
              <div className="flex gap-3 items-center">
                <span className="text-lg font-medium text-muted-foreground">{currency}</span>
                <Input
                  type="number"
                  step="0.01"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="h-12 text-lg font-semibold"
                  placeholder="0.00"
                  autoFocus
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1" 
                onClick={() => setEditPopoverOpen(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button 
                className="flex-1" 
                onClick={handleSaveBudget}
              >
                {t('common.save')}
              </Button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      {/* See Breakdown - Collapsible (Progressive Disclosure) */}
      <Collapsible open={showDetails} onOpenChange={setShowDetails}>
        <CollapsibleTrigger asChild>
          <button 
            className="w-full py-3 px-6 flex items-center justify-center gap-2 text-sm text-muted-foreground hover:bg-accent/50 transition-colors border-t"
            aria-expanded={showDetails}
          >
            {showDetails ? (
              <>Hide breakdown <ChevronUp className="h-4 w-4" /></>
            ) : (
              <>See breakdown <ChevronDown className="h-4 w-4" /></>
            )}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <AnimatePresence>
            {showDetails && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="px-6 pb-4 space-y-2 text-sm border-t"
              >
                <div className="pt-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Monthly budget</span>
                    <span className="font-medium">{formatCurrency(monthlyBudget, currency)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Spent so far</span>
                    <span className="font-medium">{formatCurrency(spent, currency)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Remaining</span>
                    <span className={`font-medium ${isOverBudget ? 'text-destructive' : 'text-primary'}`}>
                      {isOverBudget ? '-' : ''}{formatCurrency(Math.abs(remaining), currency)}
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="text-muted-foreground">Days left in month</span>
                    <span className="font-medium">{daysLeft}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Daily budget</span>
                    <span className={`font-medium ${getDailyBudgetColor()}`}>
                      {formatCurrency(dailyBudget, currency)}
                    </span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </CollapsibleContent>
      </Collapsible>
    </DashboardCard>
  );
};
